import { useEffect } from "react";
import { useState } from "react";

import { css } from "@compiled/react";
import { bind, type UnbindFn } from "bind-event-listener";
import { defineMessages, FormattedMessage } from "react-intl";

import AkHeading from "@atlaskit/heading";
import { Box, Stack, xcss } from "@atlaskit/primitives";
import { N0 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { ErrorView } from "@/components/signup/post-order/src/components/error-view/error-view";
import { Loading } from "@/components/signup/post-order/src/components/page-loading/page-loading";

import { EmptyStatePage } from "./forms/all-enabled-orgs";
import { OrganizationsContainer } from "./forms/organizations";
import { type DirectBuyOrgSetupProps } from "./types";
import { TopNav } from "./ui/top-nav/top-nav";

const orgSelectorWrapperStyles = xcss({
    width: "100%",
    overflowY: "scroll"
});

const containerStyles = css({
    "backgroundRepeat": "no-repeat",
    "backgroundSize": "contain",
    "height": "100vh",
    "display": "flex",
    "alignItems": "center",
    "flexDirection": "column",
    // This overwrites a global style `svg {margin-bottom: 16px}` set in magnolia
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    "& svg": {
        marginBlockEnd: 0
    },
    // TODO: Use new breakpoints, info at https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples
    "@media (max-width: 664px)": {
        // This property is important to override the inline styling that sets the bg image.
        // Image is received as a prop and therefore cannot be included in the css styles within
        // this object.

        backgroundImage: "none !important"
    }
});

const errorPageContainerStyles = css({
    // This overwrites a global style `svg {margin-bottom: 16px}` set in magnolia
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
    "& svg": {
        marginBlockEnd: 0
    }
});

const innerContainerStyles = css({
    "alignItems": "center",
    "display": "flex",
    "flexDirection": "column",
    "height": "calc(100vh - 52px)", // subtracting size of header
    "@media (max-width: 1194px)": {
        width: "100%"
    }
});

const orgSetupContainerStyles = css({
    "height": "665px",
    "backgroundColor": token("elevation.surface.sunken", N0),
    "boxShadow": `0px 8px 12px rgba(9, 30, 66, 0.15), 0px 0px 1px rgba(9, 30, 66, 0.31)`,
    // TODO Delete this comment after verifying space token -> previous value `'3px'`
    "borderRadius": token("radius.small", "3px"),
    "display": "flex",
    "padding": "0",
    "position": "relative",
    "insetBlockStart": token("space.1000", "80px"),
    "width": "1130px",
    "@media (max-width: 1194px)": {
        marginInlineStart: token("space.400", "32px"),
        marginInlineEnd: token("space.400", "32px"),
        maxWidth: "calc(100% - 64px)"
    },
    "@media (max-width: 1024px)": {
        insetBlockStart: token("space.800", "64px")
    },
    "@media (max-width: 768px)": {
        display: "flex",
        flexDirection: "column",
        height: "1055px",
        insetBlockStart: token("space.500", "36px"),
        width: "565px"
    },
    "@media (max-width: 664px)": {
        insetBlockStart: token("space.400", "32px")
    },
    "@media (max-width: 375px)": {
        boxShadow: "none",
        margin: 0,
        maxWidth: "unset",
        insetBlockStart: 0,
        width: "100%"
    }
});

const orgSetupFormContainerStyles = css({
    "display": "flex",
    "boxSizing": "border-box",
    "padding": token("space.800", "64px"),
    "@media (max-width: 1024px)": {
        padding: token("space.400", "32px")
    },
    "@media (max-width: 768px)": {
        height: "auto",
        padding: token("space.800", "64px"),
        width: "100%"
    },
    "@media (max-width: 375px)": {
        padding: token("space.400", "32px")
    }
});

const formImageStyles = css({
    "borderRadius": `0 ${token("radius.small", "3px")} ${token(
        "radius.small",
        "3px"
    )} 0`,
    "height": "100%",
    "width": "50%",
    "@media (max-width: 768px)": {
        borderRadius: `0 0 ${token("radius.small", "3px")} ${token(
            "radius.small",
            "3px"
        )}`,
        height: "665px",
        width: "100%"
    },
    "@media (max-width: 375px)": {
        borderRadius: 0
    }
});

function createBackgroundStyles(backgroundImageUrl: string) {
    return {
        backgroundImage: `url(${backgroundImageUrl})`
    };
}

export const DirectBuyOrgSetup = ({
    imageAssets,
    error,
    orgs = [],
    testId,
    orderId,
    transactionAccount,
    isLoading,
    isLastItemLoading,
    isUserLoggedIn,
    isSubmitting,
    orgBasedProduct,
    offeringIds,
    traceId,
    activateForExistingOrg,
    getNextPageOrgs
}: DirectBuyOrgSetupProps) => {
    const [selectedOrgId, setSelectedOrgId] = useState("");

    const orgSelectHandler = (orgId: string) => {
        setSelectedOrgId(orgId);
    };

    const nextPageHandler = () => {
        getNextPageOrgs();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleScrollHandler = (e: any) => {
        const bottom =
            Math.abs(
                e.target.scrollHeight -
                    (e.target.scrollTop + e.target.clientHeight)
            ) <= 1;

        if (bottom) {
            nextPageHandler();
        }
    };

    useEffect(() => {
        if (selectedOrgId) {
            activateForExistingOrg({ orgId: selectedOrgId });
        }
    }, [selectedOrgId]);

    const renderBody = () => {
        const getSubtitle = () => {
            if (!orgBasedProduct) {
                return DirectBuyOrgSetup.messages.rovoSubtitle; // fallback to rovoSubtitle if orgBasedProduct is not set
            }
            switch (true) {
                case orgBasedProduct.startsWith("GUARD"):
                    return DirectBuyOrgSetup.messages.guardSubtitle;
                case orgBasedProduct.startsWith("ROVO_CREDITS_PACK"):
                    return DirectBuyOrgSetup.messages.rovoCreditsPackSubtitle;
                case orgBasedProduct.startsWith("ROVO"):
                    return DirectBuyOrgSetup.messages.rovoSubtitle;
                case orgBasedProduct.startsWith("TWC"):
                    return DirectBuyOrgSetup.messages.twcSubtitle;
                case orgBasedProduct.startsWith("STRATEGY"):
                    return DirectBuyOrgSetup.messages.strategySubtitle;
                case orgBasedProduct === "CUSTOMER_MANAGED_KEYS" ||
                    orgBasedProduct ===
                        "CUSTOMER_MANAGED_KEYS_BILLED_SEPARATELY":
                    return DirectBuyOrgSetup.messages
                        .customerManagedKeysSubtitle;
                case orgBasedProduct.startsWith("SERVICE_COLLECTION"):
                    return DirectBuyOrgSetup.messages.serviceCollectionSubtitle;
                case orgBasedProduct.startsWith("INDEXED_OBJECTS_PACK"):
                    return DirectBuyOrgSetup.messages
                        .indexedObjectsPackSubtitle;
                default:
                    return DirectBuyOrgSetup.messages.rovoSubtitle; // default to rovoSubtitle if no match
            }
        };

        const subtitle = getSubtitle();

        if (orgs.length === 0) {
            return <EmptyStatePage />;
        }

        return (
            <Stack alignInline="center" space="space.300">
                <Stack space="space.200" alignInline="center">
                    {/* eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis */}
                    <AkHeading level="h700">
                        <FormattedMessage
                            {...DirectBuyOrgSetup.messages.title}
                        />
                    </AkHeading>
                    <Box>
                        <FormattedMessage {...subtitle} />
                    </Box>
                </Stack>

                <Box
                    testId="org-container"
                    xcss={orgSelectorWrapperStyles}
                    onScroll={handleScrollHandler}
                >
                    <OrganizationsContainer
                        offeringIds={offeringIds}
                        organizations={orgs}
                        selectHandler={orgSelectHandler}
                        isLastItemLoading={isLastItemLoading}
                        isSubmitting={Boolean(isSubmitting)}
                    />
                </Box>
            </Stack>
        );
    };

    useEffect(() => {
        function handleResize() {
            const urlPreview = document.getElementById("url-preview");
            const form = document.getElementById("form");

            if (!form || !urlPreview) {
                return;
            }

            if (window.innerWidth > 768) {
                urlPreview.style.top = "";
                return;
            }

            const { height } = form.getBoundingClientRect();
            urlPreview.style.top = `${height + 137}px`;
        }

        const unbind: UnbindFn = bind(window, {
            type: "resize",
            listener: handleResize
        });

        return unbind;
    }, []);

    if (isLoading) {
        return <Loading message="Loading..." />;
    }

    const showError = error || !orderId || !transactionAccount;

    return (
        <div
            style={createBackgroundStyles(imageAssets.background)}
            css={containerStyles}
            data-testid={testId}
        >
            {!!isUserLoggedIn && <TopNav />}
            <div css={innerContainerStyles}>
                {!!showError && (
                    <div css={errorPageContainerStyles}>
                        <ErrorView traceId={traceId} />
                    </div>
                )}
                {!showError && (
                    <div css={orgSetupContainerStyles}>
                        <div id="form" css={orgSetupFormContainerStyles}>
                            {renderBody()}
                        </div>
                        <div
                            css={formImageStyles}
                            style={createBackgroundStyles(imageAssets.browser)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

DirectBuyOrgSetup.messages = defineMessages({
    title: {
        id: "Wac.Signup.PostOrder.Rovo.organization.page.title",
        defaultMessage: "Select an organization",
        description: "Description text for user to select an organization"
    },
    rovoSubtitle: {
        id: "Wac.Signup.PostOrder.Rovo.organization.page.subtitle",
        defaultMessage:
            "Select an existing organization and start a trial of Rovo.",
        description:
            "Message on rovo-signup landing page telling users what to do to get started"
    },
    rovoCreditsPackSubtitle: {
        id: "Wac.Signup.PostOrder.RovoCreditsPack.organization.page.subtitle",
        defaultMessage:
            "Select an existing organization and start Rovo Credits Pack.",
        description:
            "Message on rovo-credits-pack-signup landing page telling users what to do to get started"
    },
    guardSubtitle: {
        id: "Wac.Signup.PostOrder.Guard.organization.page.subtitle",
        defaultMessage: "Select an existing organization and start Guard.",
        description:
            "Message on guard-signup landing page telling users what to do to get started"
    },
    strategySubtitle: {
        id: "Wac.Signup.PostOrder.Strategy.organization.page.subtitle",
        defaultMessage: "Select an existing organization and start Strategy.",
        description:
            "Message on strategy-signup landing page telling users what to do to get started"
    },
    twcSubtitle: {
        id: "Wac.Signup.PostOrder.TWC.organization.page.subtitle",
        defaultMessage: "Select an existing organization and start TWC",
        description:
            "Message on twc-signup landing page telling users what to do to get started"
    },
    customerManagedKeysSubtitle: {
        id: "Wac.Signup.PostOrder.CustomerManagedKeys.organization.page.subtitle",
        defaultMessage:
            "Select an existing organization and start Customer Managed Keys.",
        description:
            "Message on customer-managed-keys-signup landing page telling users what to do to get started"
    },
    serviceCollectionSubtitle: {
        id: "Wac.Signup.PostOrder.ServiceCollection.organization.page.subtitle",
        defaultMessage:
            "Select an existing organization and start Service Collection.",
        description:
            "Message on service-collection-signup landing page telling users what to do to get started"
    },
    indexedObjectsPackSubtitle: {
        id: "Wac.Signup.PostOrder.IndexedObjectsPack.organization.page.subtitle",
        defaultMessage:
            "Select an existing organization and start Indexed Objects Pack.",
        description:
            "Message on indexed-objects-pack-signup landing page telling users what to do to get started"
    }
});
